import type { IncomingLeadPayload } from "./leadTypes";
import { normalizeLeadTypeId } from "./leadTypeIds";

const toBool = (value: unknown): boolean | undefined => {
  if (value === undefined || value === null) return undefined;
  if (typeof value === "boolean") return value;
  if (typeof value === "string") {
    const v = value.toLowerCase().trim();
    if (["yes", "y", "true", "1"].includes(v)) return true;
    if (["no", "n", "false", "0"].includes(v)) return false;
  }
  if (typeof value === "number") return value > 0;
  return undefined;
};

const pick = (payload: IncomingLeadPayload, keys: string[]) => {
  for (const key of keys) {
    if ((payload as any)[key] !== undefined) return (payload as any)[key];
  }
  return undefined;
};

export function normalizeLeadProfile(payload: IncomingLeadPayload, leadTypeId: string): Record<string, unknown> {
  const canonicalLeadTypeId = normalizeLeadTypeId(leadTypeId);
  const common = {
    first_name: pick(payload, ["first_name", "firstName", "First Name", "first"]),
    last_name: pick(payload, ["last_name", "lastName", "Last Name", "last"]),
    phone: pick(payload, ["phone", "phone_number", "Phone"]).toString?.() ?? pick(payload, ["phone", "phone_number", "Phone"]),
    email: pick(payload, ["email", "Email"]),
    state: (pick(payload, ["state", "State"]) || "") as string,
    utm_source: pick(payload, ["utm_source", "utmSource"]),
    utm_medium: pick(payload, ["utm_medium", "utmMedium"]),
    utm_campaign: pick(payload, ["utm_campaign", "utmCampaign"]),
    utm_content: pick(payload, ["utm_content", "utmContent"]),
    otp_verified: toBool(
      pick(payload, ["otp_verified", "otpVerified", "OTP Verified", "is_verified_lead"]),
    ) ?? (pick(payload, ["otp_verification__otp_field", "otp_field"]) ? true : false),
  } as Record<string, unknown>;

  switch (canonicalLeadTypeId) {
    case "mortgage_purchase":
      return {
        ...common,
        goal: pick(payload, ["goal", "goal_description", "Which one best describes your goal?"] ),
        process_stage: pick(payload, ["process_stage", "Where are you in the process right now?"] ),
        employment_status: pick(payload, ["employment_status", "What’s your work status?"] ),
        co_borrower: toBool(pick(payload, ["co_borrower", "Will someone else be applying with you as a co-borrower?"] )),
        purchase_price_range: pick(payload, ["purchase_price_range", "What price range are you looking at for the new house?"] ),
        credit_quality: pick(payload, ["credit", "How would you describe your credit?"] ),
        late_payments_12mo: pick(payload, ["late_payments_12mo", "Number of late mortgage payments in the past 12 months? (credit cards, car, loans)"] ),
        collections_or_public_records: pick(payload, ["collections", "Do you have any collections, charge‑offs, or public records (like a judgment or bankruptcy)?"] ),
        new_credit_6mo: pick(payload, ["new_credit_6mo", "Have you applied for several new credit cards or loans in the past 6 months?"] ),
        household_income: pick(payload, ["household_income", "And just to get a sense of the numbers, what’s your total household income per year (before taxes)?"] ),
        down_payment_savings: pick(payload, ["down_payment", "How much do you currently have saved for a down payment?"] ),
      };
    case "mortgage_refi":
      return {
        ...common,
        refi_goal: pick(payload, ["refi_goal", "What are you hoping to achieve by refinancing?"] ),
        owns_home: toBool(pick(payload, ["owns_home", "Just to confirm, do you currently own a home?"] )),
        property_type: pick(payload, ["property_type", "What type of property are you refinancing?"] ),
        mortgage_balance_est: pick(payload, ["mortgage_balance", "What’s your current mortgage balance? No need to be exact, just your best guess is fine!"] ),
        home_value_est: pick(payload, ["home_value", "What do you think your home is worth today? It’s okay to estimate, we’ll help confirm it later."] ),
        employment_status: pick(payload, ["employment_status", "What’s your work status?"] ),
        co_borrower: toBool(pick(payload, ["co_borrower", "Will someone else be applying with you as a co-borrower?"] )),
        credit_quality: pick(payload, ["credit", "How would you describe your credit?"] ),
        late_payments_12mo: pick(payload, ["late_payments_12mo", "Number of late mortgage payments in the past 12 months? (credit cards, car, loans)"] ),
        collections_or_public_records: pick(payload, ["collections", "Do you have any collections, charge‑offs, or public records (like a judgment or bankruptcy)?"] ),
        new_credit_6mo: pick(payload, ["new_credit_6mo", "Have you applied for several new credit cards or loans in the past 6 months?"] ),
        household_income: pick(payload, ["household_income", "And just to get a sense of the numbers, what’s your total household income per year (before taxes)?"] ),
      };
    case "mortgage_heloc":
    case "mortgage_reverse":
      return {
        ...common,
        equity_goal: pick(payload, ["equity_goal", "What’s your main goal with accessing your home equity?"] ),
        owns_home: toBool(pick(payload, ["owns_home", "Just to confirm, do you currently own a home?"] )),
        timing: pick(payload, ["timing", "When are you hoping to access those funds or see the benefits?"] ),
        property_state: pick(payload, ["property_state", "Which state is your home in?"] ),
        property_type: pick(payload, ["property_type", "What type of property are you looking to pull equity from?"] ),
        home_value_est: pick(payload, ["home_value", "About how much do you think your home is worth today? Just a rough estimate is totally fine."] ),
        mortgage_balance_est: pick(payload, ["mortgage_balance", "And roughly how much do you still owe on your mortgage?"] ),
        age_range: pick(payload, ["age_range", "What’s your age range?"] ),
        credit_quality: pick(payload, ["credit", "How would you describe your credit?"] ),
        late_payments_12mo: pick(payload, ["late_payments_12mo", "Number of late mortgage payments in the past 12 months? (credit cards, car, loans)"] ),
        collections_or_public_records: pick(payload, ["collections", "Do you have any collections, charge‑offs, or public records (like a judgment or bankruptcy)?"] ),
        household_income: pick(payload, ["household_income", "And just to get a sense of the numbers, what’s your total household income per year (before taxes)?"] ),
      };
    case "life_iul":
      return {
        ...common,
        iul_goal: pick(payload, ["iul_goal", "What are you looking for with life insurance?"] ),
        coverage_amount: pick(payload, ["coverage_amount", "How much coverage are you roughly looking for?"] ),
        age: pick(payload, ["age", "What’s your age?"] ),
        annual_income: pick(payload, ["annual_income", "What’s your approximate annual income?"] ),
        monthly_budget: pick(payload, ["monthly_budget", "If this strategy makes sense, how much could you comfortably allocate per month?"] ),
        health_status: pick(payload, ["health_status", "How would you describe your overall health?"] ),
        tobacco_use: pick(payload, ["tobacco_use", "Do you currently use tobacco or nicotine products?"] ),
      };
    case "life_mp":
      return {
        ...common,
        mp_reason: pick(payload, ["mp_reason", "What’s your main reason for looking into mortgage protection?"] ),
        has_mortgage_or_buying: pick(payload, ["has_mortgage", "Do you currently have a mortgage or are you in the process of buying a home?"] ),
        mortgage_balance_est: pick(payload, ["mortgage_balance", "About how much do you still owe on your mortgage?"] ),
        beneficiary: pick(payload, ["beneficiary", "Who would you want to protect with this coverage?"] ),
        timeline: pick(payload, ["timeline", "If everything makes sense, when would you like to move forward with your MP?"] ),
        dob: pick(payload, ["dob", "What’s your Date of Birth?"] ),
        health_status: pick(payload, ["health_status", "How would you describe your overall health?"] ),
      };
    case "life_fex":
      return {
        ...common,
        fex_reason: pick(payload, ["fex_reason", "What's your main reason for looking into final expense coverage?"] ),
        applied_life_insurance_before: pick(payload, [
          "applied_life_insurance_before",
          "life_insurance_applied_before",
          "applied_for_life_insurance",
          "prior_life_insurance",
        ]),
        age_range: pick(payload, ["age_range", "What's your age range?"] ),
        coverage_amount: pick(payload, ["coverage_amount", "How much coverage are you looking for?"] ),
        monthly_budget: pick(payload, ["monthly_budget", "What is your monthly budget for coverage?"] ),
        timeline: pick(payload, ["timeline", "If everything makes sense, when would you like to get started?"] ),
        health_status: pick(payload, ["health_status", "How would you describe your overall health?"] ),
      };
    default:
      return { ...common };
  }
}
